import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const Tables = () => {
  const navigate = useNavigate();
  const [activeForm, setActiveForm] = useState('skills');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Form states
  const [recruiterData, setRecruiterData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    location: ''
  });

  const [skillData, setSkillData] = useState({
    skillName: '',
    proficiency: 'Beginner'
  });

  const [interviewData, setInterviewData] = useState({
    companyName: '',
    date: '',
    mode: 'Technical',
    result: 'Pending'
  });

  const [placementData, setPlacementData] = useState({
    companyName: '',
    jobRole: '',
    salary: '',
    joiningDate: ''
  });

  const [internshipData, setInternshipData] = useState({
    companyName: '',
    role: '',
    duration: '',
    stipend: '',
    startDate: ''
  });

  // Handle form submissions
  const handleRecruiterSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      console.log('Adding recruiter:', recruiterData); // Debug log

      const response = await api.post('/recruiters', recruiterData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Recruiter added:', response.data); // Debug log
      setMessage('✅ Recruiter added successfully!');
      setRecruiterData({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        location: ''
      });
    } catch (error) {
      setMessage('❌ Error: ' + (error.response?.data?.message || 'Failed to add recruiter'));
      console.error('Recruiter error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkillSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const usn = localStorage.getItem('usn'); // ✅ Get USN from localStorage
      
      console.log('Adding skill with USN:', usn); // Debug log

      if (!usn) {
        setMessage('❌ Error: USN not found. Please login again.');
        return;
      }

      const response = await api.post('/skills', {
        ...skillData,
        student: usn // ✅ Send USN as student field
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Skill added:', response.data); // Debug log
      setMessage('✅ Skill added successfully!');
      setSkillData({ skillName: '', proficiency: 'Beginner' });
    } catch (error) {
      setMessage('❌ Error: ' + (error.response?.data?.message || 'Failed to add skill'));
      console.error('Skill error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInterviewSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const usn = localStorage.getItem('usn'); // ✅ Get USN from localStorage
      
      console.log('Adding interview with USN:', usn); // Debug log

      if (!usn) {
        setMessage('❌ Error: USN not found. Please login again.');
        return;
      }

      const response = await api.post('/interviews', {
        ...interviewData,
        student: usn // ✅ Send USN as student field
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Interview added:', response.data); // Debug log
      setMessage('✅ Interview added successfully!');
      setInterviewData({
        companyName: '',
        date: '',
        mode: 'Technical',
        result: 'Pending'
      });
    } catch (error) {
      setMessage('❌ Error: ' + (error.response?.data?.message || 'Failed to add interview'));
      console.error('Interview error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlacementSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const usn = localStorage.getItem('usn'); // ✅ Get USN from localStorage
      
      console.log('Adding placement with USN:', usn); // Debug log

      if (!usn) {
        setMessage('❌ Error: USN not found. Please login again.');
        return;
      }

      const response = await api.post('/placements', {
        ...placementData,
        student: usn // ✅ Send USN as student field
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Placement added:', response.data); // Debug log
      setMessage('✅ Placement added successfully!');
      setPlacementData({
        companyName: '',
        jobRole: '',
        salary: '',
        joiningDate: ''
      });
    } catch (error) {
      setMessage('❌ Error: ' + (error.response?.data?.message || 'Failed to add placement'));
      console.error('Placement error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInternshipSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const usn = localStorage.getItem('usn'); // ✅ Get USN from localStorage
      
      console.log('Adding internship with USN:', usn); // Debug log

      if (!usn) {
        setMessage('❌ Error: USN not found. Please login again.');
        return;
      }

      const response = await api.post('/internships', {
        ...internshipData,
        student: usn // ✅ Send USN as student field
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Internship added:', response.data); // Debug log
      setMessage('✅ Internship added successfully!');
      setInternshipData({
        companyName: '',
        role: '',
        duration: '',
        stipend: '',
        startDate: ''
      });
    } catch (error) {
      setMessage('❌ Error: ' + (error.response?.data?.message || 'Failed to add internship'));
      console.error('Internship error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📝 Add Student Data</h1>
        <div style={styles.headerButtons}>
          <button onClick={() => navigate('/student-dashboard')} style={styles.viewButton}>
            👁️ View Dashboard
          </button>
          <button onClick={handleLogout} style={styles.logoutButton}>
            🚪 Logout
          </button>
        </div>
      </div>

      {/* Tab Buttons */}
      <div style={styles.tabContainer}>
        <button
          onClick={() => setActiveForm('skills')}
          style={activeForm === 'skills' ? styles.activeTab : styles.tab}
        >
          ⚡ Skills
        </button>
        <button
          onClick={() => setActiveForm('recruiter')}
          style={activeForm === 'recruiter' ? styles.activeTab : styles.tab}
        >
          🏢 Recruiters
        </button>
        <button
          onClick={() => setActiveForm('interviews')}
          style={activeForm === 'interviews' ? styles.activeTab : styles.tab}
        >
          🎯 Interviews
        </button>
        <button
          onClick={() => setActiveForm('placements')}
          style={activeForm === 'placements' ? styles.activeTab : styles.tab}
        >
          💼 Placements
        </button>
        <button
          onClick={() => setActiveForm('internships')}
          style={activeForm === 'internships' ? styles.activeTab : styles.tab}
        >
          📝 Internships
        </button>
      </div>

      {/* Forms */}
      <div style={styles.formContainer}>
        {message && <div style={message.includes('✅') ? styles.successMessage : styles.errorMessage}>{message}</div>}

        {/* Skills Form */}
        {activeForm === 'skills' && (
          <form onSubmit={handleSkillSubmit} style={styles.form}>
            <h2 style={styles.formTitle}>⚡ Add Skill</h2>
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>Skill Name *</label>
              <input
                type="text"
                value={skillData.skillName}
                onChange={(e) => setSkillData({...skillData, skillName: e.target.value})}
                style={styles.input}
                placeholder="e.g., React, Java, Python"
                required
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Proficiency Level *</label>
              <select
                value={skillData.proficiency}
                onChange={(e) => setSkillData({...skillData, proficiency: e.target.value})}
                style={styles.input}
                required
              >
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
                <option value="Expert">Expert</option>
              </select>
            </div>

            <button type="submit" style={styles.submitButton} disabled={loading}>
              {loading ? 'Adding...' : 'Add Skill'}
            </button>
          </form>
        )}

        {/* Recruiter Form */}
        {activeForm === 'recruiter' && (
          <form onSubmit={handleRecruiterSubmit} style={styles.form}>
            <h2 style={styles.formTitle}>🏢 Add Recruiter</h2>
            
            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Company Name *</label>
                <input
                  type="text"
                  value={recruiterData.companyName}
                  onChange={(e) => setRecruiterData({...recruiterData, companyName: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., Google, Microsoft"
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Contact Person *</label>
                <input
                  type="text"
                  value={recruiterData.contactPerson}
                  onChange={(e) => setRecruiterData({...recruiterData, contactPerson: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., John Doe"
                  required
                />
              </div>
            </div>

            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Email *</label>
                <input
                  type="email"
                  value={recruiterData.email}
                  onChange={(e) => setRecruiterData({...recruiterData, email: e.target.value})}
                  style={styles.input}
                  placeholder="recruiter@company.com"
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Phone *</label>
                <input
                  type="tel"
                  value={recruiterData.phone}
                  onChange={(e) => setRecruiterData({...recruiterData, phone: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., 9876543210"
                  required
                />
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Location</label>
              <input
                type="text"
                value={recruiterData.location}
                onChange={(e) => setRecruiterData({...recruiterData, location: e.target.value})}
                style={styles.input}
                placeholder="e.g., Bangalore, India"
              />
            </div>

            <button type="submit" style={styles.submitButton} disabled={loading}>
              {loading ? 'Adding...' : 'Add Recruiter'}
            </button>
          </form>
        )}

        {/* Interview Form */}
        {activeForm === 'interviews' && (
          <form onSubmit={handleInterviewSubmit} style={styles.form}>
            <h2 style={styles.formTitle}>🎯 Add Interview</h2>
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>Company Name *</label>
              <input
                type="text"
                value={interviewData.companyName}
                onChange={(e) => setInterviewData({...interviewData, companyName: e.target.value})}
                style={styles.input}
                placeholder="e.g., Google, Microsoft"
                required
              />
            </div>

            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Interview Date *</label>
                <input
                  type="datetime-local"
                  value={interviewData.date}
                  onChange={(e) => setInterviewData({...interviewData, date: e.target.value})}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Interview Mode *</label>
                <select
                  value={interviewData.mode}
                  onChange={(e) => setInterviewData({...interviewData, mode: e.target.value})}
                  style={styles.input}
                  required
                >
                  <option value="Technical">Technical</option>
                  <option value="HR">HR</option>
                  <option value="Managerial">Managerial</option>
                  <option value="Group Discussion">Group Discussion</option>
                </select>
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Result *</label>
              <select
                value={interviewData.result}
                onChange={(e) => setInterviewData({...interviewData, result: e.target.value})}
                style={styles.input}
                required
              >
                <option value="Pending">Pending</option>
                <option value="Selected">Selected</option>
                <option value="Rejected">Rejected</option>
                <option value="On Hold">On Hold</option>
              </select>
            </div>

            <button type="submit" style={styles.submitButton} disabled={loading}>
              {loading ? 'Adding...' : 'Add Interview'}
            </button>
          </form>
        )}

        {/* Placement Form */}
        {activeForm === 'placements' && (
          <form onSubmit={handlePlacementSubmit} style={styles.form}>
            <h2 style={styles.formTitle}>💼 Add Placement</h2>
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>Company Name *</label>
              <input
                type="text"
                value={placementData.companyName}
                onChange={(e) => setPlacementData({...placementData, companyName: e.target.value})}
                style={styles.input}
                placeholder="e.g., Amazon, TCS"
                required
              />
            </div>

            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Job Role *</label>
                <input
                  type="text"
                  value={placementData.jobRole}
                  onChange={(e) => setPlacementData({...placementData, jobRole: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., Software Engineer"
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Salary (LPA) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={placementData.salary}
                  onChange={(e) => setPlacementData({...placementData, salary: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., 12.5"
                  required
                />
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Joining Date *</label>
              <input
                type="date"
                value={placementData.joiningDate}
                onChange={(e) => setPlacementData({...placementData, joiningDate: e.target.value})}
                style={styles.input}
                required
              />
            </div>

            <button type="submit" style={styles.submitButton} disabled={loading}>
              {loading ? 'Adding...' : 'Add Placement'}
            </button>
          </form>
        )}

        {/* Internship Form */}
        {activeForm === 'internships' && (
          <form onSubmit={handleInternshipSubmit} style={styles.form}>
            <h2 style={styles.formTitle}>📝 Add Internship</h2>
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>Company Name *</label>
              <input
                type="text"
                value={internshipData.companyName}
                onChange={(e) => setInternshipData({...internshipData, companyName: e.target.value})}
                style={styles.input}
                placeholder="e.g., Flipkart, Intel"
                required
              />
            </div>

            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Role *</label>
                <input
                  type="text"
                  value={internshipData.role}
                  onChange={(e) => setInternshipData({...internshipData, role: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., Web Developer Intern"
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Duration (months) *</label>
                <input
                  type="number"
                  value={internshipData.duration}
                  onChange={(e) => setInternshipData({...internshipData, duration: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., 6"
                  required
                />
              </div>
            </div>

            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Stipend (₹/month) *</label>
                <input
                  type="number"
                  value={internshipData.stipend}
                  onChange={(e) => setInternshipData({...internshipData, stipend: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., 15000"
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Start Date *</label>
                <input
                  type="date"
                  value={internshipData.startDate}
                  onChange={(e) => setInternshipData({...internshipData, startDate: e.target.value})}
                  style={styles.input}
                  required
                />
              </div>
            </div>

            <button type="submit" style={styles.submitButton} disabled={loading}>
              {loading ? 'Adding...' : 'Add Internship'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '2rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  title: {
    color: 'white',
    fontSize: '2.5rem',
    fontWeight: '700',
    margin: 0,
  },
  headerButtons: {
    display: 'flex',
    gap: '1rem',
  },
  viewButton: {
    padding: '0.8rem 1.5rem',
    background: 'white',
    color: '#667eea',
    border: 'none',
    borderRadius: '10px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  logoutButton: {
    padding: '0.8rem 1.5rem',
    background: '#f56565',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  tabContainer: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    flexWrap: 'wrap',
  },
  tab: {
    padding: '1rem 2rem',
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  activeTab: {
    padding: '1rem 2rem',
    background: 'white',
    color: '#667eea',
    border: 'none',
    borderRadius: '10px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  formContainer: {
    background: 'white',
    borderRadius: '15px',
    padding: '2rem',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  },
  form: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  formTitle: {
    fontSize: '1.8rem',
    color: '#333',
    marginBottom: '2rem',
    textAlign: 'center',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.5rem',
    marginBottom: '1.5rem',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '1.5rem',
  },
  label: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#555',
    marginBottom: '0.5rem',
  },
  input: {
    padding: '0.8rem 1rem',
    border: '2px solid #ddd',
    borderRadius: '10px',
    fontSize: '1rem',
    outline: 'none',
    transition: 'border 0.3s ease',
  },
  submitButton: {
    width: '100%',
    padding: '1rem',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '1rem',
  },
  successMessage: {
    padding: '1rem',
    background: '#d4edda',
    color: '#155724',
    borderRadius: '10px',
    marginBottom: '1.5rem',
    textAlign: 'center',
    fontWeight: '600',
  },
  errorMessage: {
    padding: '1rem',
    background: '#f8d7da',
    color: '#721c24',
    borderRadius: '10px',
    marginBottom: '1.5rem',
    textAlign: 'center',
    fontWeight: '600',
  },
};

export default Tables;
